import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { IProfessionalProfile, AdditionalMediaInfo } from 'types/newUniprofile';
import { Form, Select, message } from 'antd';
import { FOREIGN_LANGUAGE } from 'constants/meta';
import s from './s.module.less';
import DefaultProfileAvatar from 'assets/common/defaultProviderAvatar.svg';
import PhotoList from 'components/PhotoList';
import VideoList from 'components/VideoList';
import { VIDEO_UPLOAD_DEMO, EMPTY_PLACEHOLDER } from 'constants/common';
import { editProfessionalProfile } from 'api/newUniprofile';

interface AdditionalMediaProps {
    initValue?: IProfessionalProfile;
    isEditing?: boolean;
    refetch: () => void;
}

const AdditionalMedia = forwardRef((props: AdditionalMediaProps, ref) => {
    const { isEditing, initValue, refetch } = props;
    const [form] = Form.useForm<AdditionalMediaInfo>();

    useEffect(() => {
        if (initValue) {
            const { additionalPhotoList, videoList, foreignLanguage, ..._ } = initValue;
            form.setFieldsValue({
                additionalPhotoList,
                videoList,
                foreignLanguage: (foreignLanguage as string)?.split(','),
            });
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const params = await form.validateFields();
            console.log('params', params);
            const result = await editProfessionalProfile({
                ...initValue,
                ...params,
                foreignLanguage: (params.foreignLanguage as string[])?.join(','),
            });
            if (result.error) {
                message.error(result.error);
            } else {
                refetch();
            }
        } catch (error) {
            console.error(error);
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => {
            return form.validateFields();
        },
    }));

    const defaultCover = initValue?.photo || DefaultProfileAvatar;
    const videoUrl = VIDEO_UPLOAD_DEMO;

    const renderEditing = () => {
        return (
            <div className="mt-4">
                <Form
                    form={form}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Form.Item
                        label="Additional photos"
                        name="additionalPhotoList"
                    >
                        <PhotoList enable min={3} data={form.getFieldValue('additionalPhotoList')} />
                    </Form.Item>
                    <Form.Item
                        label="Videos"
                        name="videoList"
                    >
                        <div className={s.subLabel}>
                            <p>Build trust with patients by adding a short video to your UniProfile. Let them see your face and hear your voice to create rapport early on.</p>
                            <p>Not sure where to start? We've got you covered! Watch the example video for ideas by clicking <a href={videoUrl} target="_blank" rel="noreferrer">the video link</a>.</p>
                        </div>
                        <VideoList
                            providerId={initValue?.providerId!}
                            defaultCover={defaultCover}
                            enable
                            max={3}
                            value={form.getFieldValue('videoList')}
                            onChange={(videos) => {
                                form.setFieldValue('videoList', videos);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Additional languages/dialects"
                        name="foreignLanguage"
                    >
                        <Select
                            mode="tags"
                            placeholder="Enter the language here"
                            style={{ width: '100%' }}
                            options={FOREIGN_LANGUAGE}
                        />
                    </Form.Item>
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        const { additionalPhotoList, videoList, foreignLanguage } = initValue || {};
        return (
            (
                <div className={s.subModule}>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={`${s.label}`}>Additional photos</div>
                            {additionalPhotoList?.length && additionalPhotoList?.length > 0 ? <PhotoList enable={false} min={3} data={additionalPhotoList} /> : <div className={s.value}>{EMPTY_PLACEHOLDER}</div>}
                        </div>
                    </div>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={`${s.label}`}>Videos</div>
                            {videoList?.length && videoList?.length > 0 ? <VideoList
                                providerId={initValue?.providerId!}
                                defaultCover={defaultCover}
                                max={3}
                                value={videoList}
                                enable={false}
                            /> : <div className={s.value}>{EMPTY_PLACEHOLDER}</div>}
                        </div>
                    </div>
                    <div className={s.row}>
                        <div
                            className={s.displayItem}
                        >
                            <div className={s.label}>Additional languages/dialects</div>
                            <div className={s.value}>{foreignLanguage || EMPTY_PLACEHOLDER}</div>
                        </div>
                    </div>
                </div>
            )
        );
    };

    return (
        <div>
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default AdditionalMedia;
